/**
 * 파일명 : Usr0302p3.jsx
 * 설명 : 프로젝트검색
 * 수정내역
 ************************************************
 * Version    수정일자      수정자    수정내용      
 ************************************************
 * 1.0       2026-03-20    황은택     jsx -> tsx 전환 작업
 * 
 */

/************************************************
 * Import 영역  
 ************************************************/
/*  React  */
import React from 'react';
import locale from 'antd/lib/locale/ko_KR';

/* Reducer */
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { Usr0302p3Action } from '../../../redux/slice/usr/usr0302p3_slice';
import { IInquiryCndsState } from '../../../redux/models/usr/usr0302p3_model';

/*  AntD  */
import { Button, Space, Row, Col, Layout, Typography, Collapse, Input, Table } from 'antd';
import { SearchOutlined, CaretRightOutlined } from '@ant-design/icons';

/*  업무  */
import * as commonProxy from 'libs/CommonProxy';
import * as common from 'libs/common';

/************************************************
 * 전역변수 영역
 ************************************************/

const Usr0302p3 = (props: any) => {
    /************************************************
     * 화면 초기화 영역  
     ************************************************/
    /**
     * 기능 : State / Reducer 선언
     * param : 
     */
    const dispatch    = useAppDispatch();
    const inquiryCnds = useAppSelector((state) => state.Usr0302p3.inquiryCnds);
    const projectList = useAppSelector((state) => state.Usr0302p3.projectList);
    const current     = useAppSelector((state) => state.Usr0302p3.current);
    const pageSize    = useAppSelector((state) => state.Usr0302p3.pageSize);

    /**
     * 기능 : componentDidMount
     * 설명 : 화면 최초 로드시에 필요한 기능을 구현한다.
     * param : 
     * return : None
     */
    React.useEffect(() => {
        // 화면 초기화
        fn_Reset();
        
        // 프로젝트 조회
        fn_projectList();

    }, []);

    /* Render 변수 선언영역 */
    /* AntD */
    const { Content } = Layout;
    const { Text }    = Typography;
    const { Panel }   = Collapse;

    /* Table Columns */
    /* 학력 현황 */
    const projectColumns = React.useCallback(() => {
        const arr: any = [
            {
                title: () => <div style={{ textAlign: 'center' }}>프로젝트코드</div>,
                dataIndex: 'pjCd',
                key:       'pjCd',
                align:     'center',
                width:     '13%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>프로젝트명</div>,
                dataIndex: 'pjNm',
                key:       'pjNm',
                align:     'center',
                width:     'auto',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>수행기간</div>,
                dataIndex: 'pjPrd',
                key:       'pjPrd',
                align:     'center',
                width:     '19%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>고객사</div>,
                dataIndex: 'cusNm',
                key:       'cusNm',
                align:     'center',
                width:     '15%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>PM</div>,
                dataIndex: 'userNm',
                key:       'userNm',
                align:     'center',
                width:     '8%',
            },
            {
                title: () => <div style={{ textAlign: 'center' }}>선택</div>,
                key: 'action',
                render: (_: any, record: any) => (
                    <Button
                        size      = 'small'
                        id        = 'btnSelect'
                        name      = 'btnSelect'
                        className = 'background-color-default-button'
                        data-id   = {record.id}
                        onClick   = {(e) => onClick(e, record)}
                    >
                        선택
                    </Button>
                ),
                align: 'center',
                width: '7%',
            }
        , ];

        return arr;
    }, []);

    /**
     * 기능 : 초기화 이벤트 선언
     * param : 
     */
    const fn_Reset = () => {
        dispatch(Usr0302p3Action.initState());
    };

    /**
     * 기능 : 조회 (학력 현황)
     * param : 
     */
     const fn_projectList = () => {
        /* 변수선언 및 셋팅 영역 */
        // 입력파라미터
        const requestData: IInquiryCndsState = {
            ...inquiryCnds
        };

        /* 트렌젝션 호출 및 결과 셋팅 */
        commonProxy.callAsyncSvc(requestData, {
            txCd: "TRUSRR0023",
            success: (responseData: any) => {
                dispatch(Usr0302p3Action.setProjectList(responseData.projectItems));
            },
        });
    };

    /**
     * 기능 : onClick 이벤트 선언
     * param : 
     */
    const onClick = async (e: any, record?: any) => {
        const buttonName = e.currentTarget.name;
        switch (buttonName) {
            
            // 조회 버튼
            case "btn_Search" : {
                // 프로젝트 조회
                fn_projectList();
                break;
            }
            
            // 선택 버튼
            case "btnSelect" : {
                if (record) {
                    props.accept(record);
                }
                props.cancel();
                break;
            }

            // 닫기 버튼
            case "btnClose" : {
                props.cancel();
                break;
            }

            default:
        }
    };

    return (
        <Layout
            style={{ width:1000, display: 'flex', justifyContent: 'center', alignItems: 'center', paddingLeft: '20px' }}>
            <Content>
                <Collapse
                    bordered         = {false}
                    defaultActiveKey = {['1']}
                    expandIcon       = {({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                    className        = "site-collapse-custom-collapse"
                >
                    <Panel
                        header    = "검색조건"
                        key       = "1"
                        className = "site-collapse-custom-panel"
                    >
                        <Row
                            style   = {{ marginTop: 10 }}
                            align   = "middle"
                            justify = "space-between"
                        >
                            <Space>
                                <Col>
                                    <Space>
                                        <label>프로젝트명</label>
                                        <Input
                                            style        = {{ width: 200 }}
                                            placeholder  = "입력하세요"
                                            value        = {inquiryCnds.pjNm}
                                            onChange     = {(e) => {dispatch(Usr0302p3Action.setInquiryCnds({ ...inquiryCnds, pjNm: e.target.value }))}}
                                            onPressEnter = {() => fn_projectList()}
                                        />
                                    </Space>
                                </Col>
                                <Col>
                                    <Space style = {{ marginLeft: 10 }}>
                                        <label>고객사</label>
                                        <Input
                                            style        = {{ width: 200 }}
                                            placeholder  = "입력하세요"
                                            value        = {inquiryCnds.cusNm}
                                            onChange     = {(e) => {dispatch(Usr0302p3Action.setInquiryCnds({ ...inquiryCnds, cusNm: e.target.value }))}}
                                            onPressEnter = {() => fn_projectList()}
                                        />
                                    </Space>
                                </Col>
                            </Space>
                            <Col flex="auto" style={{ textAlign: 'right' }}>
                                <Space>
                                    <Button
                                        icon      = {<SearchOutlined />}
                                        name      = "btn_Search"
                                        className = "background-color-primary-button"
                                        onClick   = {onClick}
                                    >
                                    조회
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    </Panel>
                </Collapse>
                <Row
                    align = "middle"
                    style = {{ height: 50 }}
                >
                    <Col>
                        <Space>
                            <img className="title-icon" alt="" />
                            <Text strong>프로젝트 목록</Text>
                        </Space>
                    </Col>
                    <Col flex = "auto" style = {{ marginRight: 15, textAlign: 'right' }}>
                    </Col>
                </Row>
                <Table
                    size       = "small"
                    id         = "projectList"
                    key        = "pjCd"
                    columns    = {projectColumns()}
                    dataSource = {projectList}
                    rowKey     = "pjCd"
                    pagination = {{
                        pageSize:  pageSize,
                        current:   current,
                        showTotal: () => '총 ' + projectList.length + ' 건',
                        onChange:  (page, pageSize) => {
                            dispatch(Usr0302p3Action.setCurrent(page));
                            dispatch(Usr0302p3Action.setPageSize(pageSize));
                        },
                    }}
                />
                <Row style = {{ textAlign: 'right' }}>
                    <Col
                        flex  = 'auto'
                        style = {{ textAlign: 'right' }}
                    >
                        <Space>
                            <Button
                                name      = "btnClose"
                                className = "background-color-default-button"
                                onClick   = {onClick}
                            >
                            닫기
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Content>
        </Layout>
    );
};

export default Usr0302p3;
